import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Alert from 'src/components/alert';
import ConvertedUnits from 'src/components/ConvertedUnits';
import Input from 'src/components/CustomInput';
import { useBatch } from 'src/hooks/services/useBatch';
import useConvertedInputHandler from 'src/hooks/useConvertedInputHandler';
import SingleMHSModuleContainer from 'src/pages/mline/sections/mhs/modules/single-mhs-module-container';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { MATERIAL_UNITS } from 'src/utils/constants';
import { convertToUserUnits, formatThreeDecimalsNumber, performConversion } from 'src/utils/conversions';

import RemainingQuantityProgressBar from './remaining-quantity-progress-bar';

const BuildTrustCycloneForm = ({
  module,
  slot,
  trustCyclone,
  handleConfirmLoadFromInput,
  inputModuleTransactions,
  progressBarProps,
  handleRefetchCyclone,
}) => {
  const moduleName = module.name;
  const moduleType = module.type;

  const mhsInputBatchUri = module?.current_batch;
  const {
    remainingQtyPercentage,
    remainingQty,
    isAllTransactionsLoading,
    isLastTransaction,
    weightLossThreshold,
    initialQty,
    reconciledQtyPercentage,
  } = progressBarProps;

  const { data: moduleBatch, isLoading: isModuleBatchLoading } = useBatch(mhsInputBatchUri, 'mhs-input-batch');

  const baseUnits = moduleBatch?.units;

  const { closeActionPanel } = useActionPanelStore();

  const tareWeightInBatchUnits = useMemo(() => {
    if (baseUnits) {
      // Convert from grams (always the stored unit) to the batch's unit
      return performConversion(module.tare_weight, MATERIAL_UNITS.GRAM, null, baseUnits);
    }
    return { quantity: 0, units: baseUnits, isConverted: false };
  }, [module.tare_weight, baseUnits]);


  const { quantity: displayedTareWeightQuantity } = useMemo(() => {
    if (baseUnits) {
      return convertToUserUnits(tareWeightInBatchUnits.quantity, baseUnits);
    }
    return { quantity: 0, units: '' };
  }, [tareWeightInBatchUnits.quantity, baseUnits]);

  const {
    baseValue: totalWeightBeforeReclaimBase,
    convertedValue: totalWeightBeforeReclaimConverted,
    handleInputChange: handleBeforeReclaimChange,
    convertedUnits: beforeUnits,
  } = useConvertedInputHandler('', baseUnits);

  const {
    baseValue: totalWeightAfterReclaimBase,
    convertedValue: totalWeightAfterReclaimConverted,
    handleInputChange: handleAfterReclaimChange,
  } = useConvertedInputHandler('', baseUnits);

  const {
    baseValue: reclaimedPowderWeightBase,
    convertedValue: reclaimedPowderWeightConverted,
    handleInputChange: handleReclaimedPowderChange,
  } = useConvertedInputHandler('', baseUnits);

  const [fieldErrors, setFieldErrors] = useState({
    totalWeightBeforeReclaim: '',
    totalWeightAfterReclaim: '',
  });

  // Validation applies only in non-trustCyclone mode.
  // We check:
  //   • Total Weight after Reclaim must be at least equal to tareWeight.
  //   • Total Weight before Reclaim must be greater than Total Weight after Reclaim.

  const validateInputs = (beforeConverted, afterConverted) => {
    if (trustCyclone) {
      const errors = { totalWeightBeforeReclaim: '', totalWeightAfterReclaim: '' };

      // Ensure Total Weight before Reclaim is provided even in trustCyclone mode
      if (!beforeConverted) {
        errors.totalWeightBeforeReclaim = "Total Weight before Reclaim is required";
      }

      setFieldErrors(errors);
      return;
    }

    const numericBefore = parseFloat(beforeConverted) || 0;
    const numericAfter = parseFloat(afterConverted) || 0;
    const errors = { totalWeightBeforeReclaim: '', totalWeightAfterReclaim: '' };

    if (numericAfter < displayedTareWeightQuantity) {
      errors.totalWeightAfterReclaim = "Total Weight after Reclaim must be at least equal to Tare Weight";
    }
    if (numericBefore <= numericAfter) {
      errors.totalWeightBeforeReclaim = "Total Weight before Reclaim must be greater than Total Weight after Reclaim";
    }
    setFieldErrors(errors);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'totalWeightBeforeReclaim') {
      handleBeforeReclaimChange(e);
      // Always validate the "before" field, regardless of trustCyclone mode
      validateInputs(value, totalWeightAfterReclaimConverted);
    } else if (name === 'totalWeightAfterReclaim') {
      handleAfterReclaimChange(e);
      validateInputs(totalWeightBeforeReclaimConverted, value);
    } else if (name === 'reclaimedPowderWeight') {
      handleReclaimedPowderChange(e);
      // In trustCyclone mode, we still need to validate the "before" field
      if (trustCyclone) {
        validateInputs(totalWeightBeforeReclaimConverted, '');
      }
    }
  };

  const overallIsValid =
    // "Total Weight Before Reclaim" must have a value and no error
    (totalWeightBeforeReclaimBase !== '' && !fieldErrors.totalWeightBeforeReclaim) &&
    (
      // If in trustCyclone mode, the above is sufficient
      trustCyclone ||
      // If not in trustCyclone mode, "Total Weight After Reclaim" must also have a value and no error
      (totalWeightAfterReclaimBase !== '' && !fieldErrors.totalWeightAfterReclaim)
    );

  // Calculate weights based on trustCyclone mode
  let reclaimedWeightBase, consumedWeightBase;

  if (trustCyclone) {
    // In trustCyclone mode, reclaimed weight comes directly from input
    reclaimedWeightBase = reclaimedPowderWeightBase !== '' ? parseFloat(reclaimedPowderWeightBase) : 0;

    // For consumed weight in trustCyclone mode:
    // If any required input is empty, set consumed to 0 to avoid negative calculations
    if (totalWeightBeforeReclaimBase === '' || totalWeightBeforeReclaimBase === null) {
      consumedWeightBase = 0;
    } else {
      const beforeWeight = parseFloat(totalWeightBeforeReclaimBase);
      // If "Total Weight before Reclaim" has a value, calculate consumed weight
      const calculatedConsumed = beforeWeight - reclaimedWeightBase - tareWeightInBatchUnits.quantity;
      // Ensure we don't get negative values
      consumedWeightBase = Math.max(0, calculatedConsumed);
    }
  } else {
    // Original calculation for non-trustCyclone mode
    if (totalWeightBeforeReclaimBase === '' || totalWeightAfterReclaimBase === '') {
      reclaimedWeightBase = 0;
      consumedWeightBase = 0;
    } else {
      reclaimedWeightBase = Math.max(0, totalWeightBeforeReclaimBase - totalWeightAfterReclaimBase);
      consumedWeightBase = Math.max(0, totalWeightAfterReclaimBase - tareWeightInBatchUnits.quantity);
    }
  }

  const reclaimedWeightBaseFormatted = formatThreeDecimalsNumber(reclaimedWeightBase);
  const consumedWeightBaseFormatted = formatThreeDecimalsNumber(consumedWeightBase);

  // Calculate total build weight (sum of reclaimed and consumed)
  const totalBuildWeight = reclaimedWeightBase + consumedWeightBase;
  const totalBuildWeightFormatted = formatThreeDecimalsNumber(totalBuildWeight);

  // Calculate percentage of total build weight relative to initial quantity
  const totalBuildPercentage = progressBarProps.initialQty > 0
    ? Math.round((totalBuildWeight / progressBarProps.initialQty) * 100)
    : 0;

  // Show weight details if we have at least some input data
  const shouldShowWeightDetails = trustCyclone
    ? totalWeightBeforeReclaimBase !== ''
    : (totalWeightBeforeReclaimBase !== '' && totalWeightAfterReclaimBase !== '');

  const requestedQuantityMoreThanAvailable = remainingQty < totalBuildWeight;

  // Weight loss threshold
  const hasEnteredValues = shouldShowWeightDetails; // This already checks if both inputs have valid values
  const isWeightLoss = isLastTransaction && hasEnteredValues && totalBuildWeight < remainingQty;
  const weightLossAmount = isWeightLoss ? remainingQty - totalBuildWeight : 0;
  const weightLossPercentage = isWeightLoss && initialQty > 0
    ? Math.round((weightLossAmount / initialQty) * 100)
    : 0;
  const isSignificantWeightLoss = isWeightLoss && weightLossPercentage > weightLossThreshold;

  const handleSubmit = async () => {
    if (overallIsValid) {
      await handleConfirmLoadFromInput(moduleType, mhsInputBatchUri, reclaimedWeightBase, consumedWeightBase)
    }
  };

  return (
    <div>
      <SingleMHSModuleContainer
        skipActions
        customStatusText={moduleName}
        module={module}
        slot={slot}
        inputModuleTransactions={inputModuleTransactions}
        handleRefetchCyclone={handleRefetchCyclone}
      >
        <div className="d-flex align-items-center justify-content-between">
          {/* TODO: GE Temporarily removed */}
          {/*<div>Expected Weight: {expectedWeight} lb</div>*/}
          {isModuleBatchLoading ? <Spinner animation="border" /> : (
            <div>Tare:&nbsp;
              <ConvertedUnits quantity={tareWeightInBatchUnits.quantity} units={tareWeightInBatchUnits.units} />
            </div>
          )}
        </div>
      </SingleMHSModuleContainer>

      <Input
        label={trustCyclone ? 'Total Module Weight Before Reclaim:' : 'Total Weight before Reclaim:'}
        type="number"
        name="totalWeightBeforeReclaim"
        value={totalWeightBeforeReclaimConverted}
        suffix={beforeUnits}
        isError={Boolean(fieldErrors.totalWeightBeforeReclaim)}
        errorMessage={fieldErrors.totalWeightBeforeReclaim}
        style={{ marginBottom: '15px' }}
        onChange={handleInputChange}
      />

      {trustCyclone ? (
        <Input
          label="Weight of the Reclaimed Powder:"
          type="number"
          name="reclaimedPowderWeight"
          value={reclaimedPowderWeightConverted}
          suffix={beforeUnits}
          isError={false}
          errorMessage=""
          style={{ marginBottom: '15px' }}
          onChange={handleInputChange}
        />
      ) : (
        <Input
          label="Total Weight after Reclaim:"
          type="number"
          name="totalWeightAfterReclaim"
          value={totalWeightAfterReclaimConverted}
          suffix={beforeUnits}
          isError={Boolean(fieldErrors.totalWeightAfterReclaim)}
          errorMessage={fieldErrors.totalWeightAfterReclaim}
          style={{ marginBottom: '15px' }}
          onChange={handleInputChange}
        />
      )}

      {shouldShowWeightDetails && (
        <div className="mb15 py-2">
          <div className="d-flex align-items-center">
            <span className="font-weight-bold">Build Total:</span>
            <ConvertedUnits
              className="spacer-left font-weight-bold"
              quantity={totalBuildWeightFormatted}
              units={baseUnits}
            />
            <span className={`ml-2 badge ${requestedQuantityMoreThanAvailable ? 'badge-danger' : 'badge-success'}`}>{totalBuildPercentage}%</span>
          </div>
          <div className="mt-1 text-left">
            <span>
              (<ConvertedUnits
                className="font-weight-bold"
                quantity={reclaimedWeightBaseFormatted}
                units={baseUnits}
            />  <span className="text-muted">Reclaimed</span> +&nbsp;
              <ConvertedUnits
                className="font-weight-bold"
                quantity={consumedWeightBaseFormatted}
                units={baseUnits}
              /> <span className="text-muted">Consumed</span>)
            </span>
          </div>
        </div>
      )}

      <RemainingQuantityProgressBar
        remainingQty={remainingQty}
        remainingQtyPercentage={remainingQtyPercentage}
        baseUnits={baseUnits}
        actionWeight={totalBuildWeight}
        isActionWeightLoading={isAllTransactionsLoading}
        showActionDetails={shouldShowWeightDetails}
        initialQty={initialQty}
        reconciledQtyPercentage={reconciledQtyPercentage}
      />

      {shouldShowWeightDetails && requestedQuantityMoreThanAvailable && (
        <Alert
          name="qr-instructions"
          variant="danger"
          className="text-left d-flex align-items-center mt15"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="font-size-22 mr15" />
          <div>
            <div>
              The quantities you have entered have exceeded the amount of
              material available to be reconciled by&nbsp;
              <ConvertedUnits
                className="font-weight-bold"
                quantity={formatThreeDecimalsNumber(totalBuildWeight - remainingQty)}
                units={baseUnits}
              />.
              Please double check your data and if an error has been made when
              reconciling a previous module, please speak to your manager.
            </div>
          </div>
        </Alert>
      )}

      {trustCyclone && overallIsValid && !reclaimedPowderWeightConverted && (
        <Alert
          name="qr-weight-loss"
          variant="warning"
          className="text-left d-flex align-items-center mt15"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="font-size-22 mr15" />
          <div>
            You did not enter a weight for Reclaimed Powder - Are you sure?
          </div>
        </Alert>
      )}

      {shouldShowWeightDetails && isSignificantWeightLoss && (
        <Alert
          name="qr-weight-loss"
          variant="warning"
          className="text-left d-flex align-items-center mt15"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="font-size-22 mr15" />
          <div>
            <div>
              There is&nbsp;
              <ConvertedUnits
                className="font-weight-bold"
                quantity={formatThreeDecimalsNumber(weightLossAmount)}
                units={baseUnits}
              />&nbsp;
              <strong>({weightLossPercentage}%)</strong> of unaccounted material after completing the last transaction.
              This exceeds the <strong>{weightLossThreshold}%</strong> threshold. Please double check your data and if this material loss is correct, please speak to your manager before completing this transaction.
            </div>
          </div>
        </Alert>
      )}

      <button
        type="button"
        className="btn btn-lg btn-primary action-btn btn-block mt60"
        disabled={!overallIsValid || requestedQuantityMoreThanAvailable}
        onClick={handleSubmit}
      >
        Update
      </button>
      <button
        type="button"
        className="btn btn-lg btn-block"
        onClick={closeActionPanel}
      >
        Cancel
      </button>
    </div>
  );
};

BuildTrustCycloneForm.propTypes = {
  /* eslint-disable camelcase */
  module: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    tare_weight: PropTypes.number,
    current_batch: PropTypes.string,
  }).isRequired,
  slot: PropTypes.string.isRequired,
  trustCyclone: PropTypes.bool.isRequired,
  handleConfirmLoadFromInput: PropTypes.func.isRequired,
  inputModuleTransactions: PropTypes.arrayOf(PropTypes.shape({
    parent_transaction: PropTypes.string,
    initial_quantity_available: PropTypes.number,
  })).isRequired,
  progressBarProps: PropTypes.shape({
    remainingQtyPercentage: PropTypes.number,
    remainingQty: PropTypes.number,
    isAllTransactionsLoading: PropTypes.bool,
    initialQty: PropTypes.number,
    reconciledQtyPercentage: PropTypes.number,
    isLastTransaction: PropTypes.bool,
    weightLossThreshold: PropTypes.number,
  }).isRequired,
  handleRefetchCyclone: PropTypes.func.isRequired,
};

export default BuildTrustCycloneForm;
